/**
 * Cost-of-Error — bank-style, 10 exploits.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  type CostQuestion,
  type CostTier,
  COST_LABEL,
  COST_OF_ERROR_BANK,
} from "@/features/quiz/lib/costOfErrorQuiz";

const OPTIONS: CostTier[] = ["cheap", "medium", "expensive"];

export function QuizCostOfError() {
  const {
    question,
    revealedAnswer,
    isCorrect,
    correctAnswer,
    stats,
    submit,
    next,
    bankPosition,
    bankSize,
    isComplete,
    restart,
  } = useQuizSession<CostQuestion, CostTier>({
    quizType: "cost_of_error",
    bank: COST_OF_ERROR_BANK,
    getCorrectAnswer: (q) => q.cost,
    getQuestionPayload: (q) => ({ exploit: q.exploit }),
  });

  return (
    <QuizLayout
      Icon={Brain}
      title="Cost-of-Error"
      subtitle="How much evidence does each exploit need?"
      description="Tests sheet 8's cost-of-error matrix. The most underused concept in exploit play: cheap exploits get applied liberally on thin reads (one orbit), expensive exploits demand multiple confirming actions. Bluffing less vs a station is cheap; hero-calling wider vs a maniac is expensive: same archetype family, opposite evidence thresholds."
      stats={stats}
      bankPosition={bankPosition}
      bankSize={bankSize}
      isComplete={isComplete}
      onRestart={restart}
    >
      <div className="rounded-xl border border-border bg-white/[0.02] p-4 text-sm text-text leading-relaxed">
        <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">
          Exploit
        </div>
        <div className="italic">"{question.exploit}"</div>
      </div>

      <p className="text-center text-text text-base">Cost of being wrong?</p>

      <div className="grid grid-cols-1 gap-3">
        {OPTIONS.map((cost) => (
          <QuizOption
            key={cost}
            value={cost}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="block text-left">{COST_LABEL[cost]}</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{COST_LABEL[correctAnswer]}</strong>
            </>
          }
          rationale={question.rationale}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
